import { TelegramPreview, usePreviewStore } from "@tgpe/editor-core";
import { useEffect, useMemo, useRef } from "react";

import { reportClientError } from "../../lib/client-error.js";
import { useMediaStore } from "../../stores/media-store.js";

function srcKind(src: string | null): string {
  if (!src) return "null";
  if (src.startsWith("data:")) return "data";
  if (src.startsWith("blob:")) return "blob";
  if (src.startsWith("http")) return "http";
  return "other";
}

/**
 * Mini App's preview surface. Reads from editor-core's `usePreviewStore`,
 * which the `PreviewBridgePlugin` (mounted inside the editor) writes to
 * on every change.
 */
export function PreviewView() {
  const state = usePreviewStore((s) => s.state);
  const mode = usePreviewStore((s) => s.mode);
  const captionLimit = usePreviewStore((s) => s.captionLimit);
  const isOverLimit = usePreviewStore((s) => s.isOverLimit);
  const linkPreviewEnabled = usePreviewStore((s) => s.linkPreviewEnabled);
  const attachment = useMediaStore((s) => s.attachment);

  const mediaPreview = useMemo(() => {
    if (!attachment) return [];
    // Source priority for the preview mosaic:
    //   1. originalDataUrl  — full-quality, in-memory only, present
    //      during the live editing session. data: URL → renders on
    //      Android Telegram where blob: doesn't.
    //   2. thumbDataUrl     — 256px persisted fallback, used after the
    //      draft is reopened (originalDataUrl is gone after restore).
    //   3. blobUrl          — last resort for non-photo kinds; on
    //      iOS/Desktop blob: in <video> works, on Android it doesn't
    //      but we have nothing better.
    const isPhoto = attachment.kind === "photo";
    const photoFullSrc = isPhoto
      ? (attachment.originalDataUrl ?? attachment.thumbDataUrl ?? null)
      : null;
    const photoThumbSrc = isPhoto
      ? (attachment.thumbDataUrl ?? attachment.originalDataUrl ?? null)
      : null;
    const hasBlob = attachment.blobUrl.length > 0;
    return [
      {
        id: attachment.id,
        thumbnailUrl: photoThumbSrc,
        previewUrl: photoFullSrc,
        objectUrl: hasBlob ? attachment.blobUrl : null,
        type: attachment.kind,
        // Restored video/audio without a local blob: show the missing-media
        // placeholder rather than a broken <video> / <img>. The slot still
        // shows filename + kind so the user knows what's attached.
        isMissing: !hasBlob && !isPhoto,
        width: 0,
        height: 0,
      },
    ];
  }, [attachment]);

  // Beacon what the mosaic is being fed, once per attachment id.
  // This isolates "broken image in preview tab" to either "we passed
  // null sources" or "Android rejected the data: URL we passed".
  const lastReportedId = useRef<string | null>(null);
  useEffect(() => {
    if (!attachment) {
      lastReportedId.current = null;
      return;
    }
    if (lastReportedId.current === attachment.id) return;
    lastReportedId.current = attachment.id;
    const item = mediaPreview[0];
    if (!item) return;
    reportClientError({
      source: "manual",
      message: `preview_view_state thumb=${srcKind(item.thumbnailUrl)} preview=${srcKind(item.previewUrl)}`,
      stack: JSON.stringify({
        tag: "preview_view_state",
        kind: attachment.kind,
        thumbnail_kind: srcKind(item.thumbnailUrl),
        thumbnail_len: item.thumbnailUrl?.length ?? 0,
        preview_kind: srcKind(item.previewUrl),
        preview_len: item.previewUrl?.length ?? 0,
        object_kind: srcKind(item.objectUrl),
        object_len: item.objectUrl?.length ?? 0,
        is_missing: item.isMissing,
        attachment_status: attachment.status,
        has_thumb: !!attachment.thumbDataUrl,
        has_original: !!attachment.originalDataUrl,
      }),
    });
  }, [attachment, mediaPreview]);

  const limit = mode === "message" ? 4096 : captionLimit;

  return (
    <div className="flex h-full flex-col bg-[var(--tg-secondary-bg)] p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--tg-hint)]">
        Preview
      </div>
      <div className="flex-1 overflow-auto rounded-lg bg-[var(--tg-bg)] p-2">
        <TelegramPreview
          html={state?.html}
          charCount={state?.charCount ?? 0}
          limit={limit}
          isOverLimit={isOverLimit}
          mediaPreview={mediaPreview}
          mode={mode}
          previewState={state}
          linkPreviewEnabled={linkPreviewEnabled}
        />
      </div>
    </div>
  );
}
